import React, { useRef, useState } from "react";
import { api, formatError, resolvePhotoUrl } from "@/lib/api";
import { toast } from "sonner";
import { Upload, ImageIcon } from "@/lib/icons";

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp";
const MAX_MB = 12;

/**
 * Uploader for the homepage hero image (the portrait card next to the
 * "Find a verified photographer" headline). Uploads directly to
 * /admin/site/hero-image and the saved URL comes back already persisted.
 */
export default function HeroImageUpload({ src, onChange, testidPrefix = "hero-image" }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const doUpload = async (f) => {
    if (!f) return;
    if (!ACCEPT.split(",").includes(f.type.toLowerCase())) {
      toast.error("Only JPG, PNG or WEBP files are allowed");
      return;
    }
    if (f.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be ${MAX_MB} MB or less`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/site/hero-image", fd);
      if (!data?.image_url) {
        throw new Error("The server did not return the uploaded hero image URL");
      }
      toast.success("Hero image updated");
      onChange?.(data.image_url);
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const resolved = resolvePhotoUrl(src);

  return (
    <div className="flex items-center gap-4">
      <div
        className="relative shrink-0 overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-br from-violet-800/40 to-magenta-700/30 flex items-center justify-center"
        style={{ width: 96, height: 120 }}
        data-testid={`${testidPrefix}-preview`}
      >
        {resolved ? (
          <img src={resolved} alt="Hero" className="w-full h-full object-cover" />
        ) : (
          <ImageIcon size={32} className="text-fuchsia-300/50" />
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center text-white text-[10px]">
            Working…
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2">
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => doUpload(e.target.files?.[0])}
          data-testid={`${testidPrefix}-input`}
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={busy}
          data-testid={`${testidPrefix}-upload-btn`}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white disabled:opacity-60"
        >
          <Upload size={12} /> {resolved ? "Change image" : "Upload image"}
        </button>
        <div className="text-[10px] text-white/40">JPG / PNG / WEBP · ≤ {MAX_MB} MB · Portrait works best</div>
      </div>
    </div>
  );
}
